import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { FreightTerms, Product } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { AddCostPackageForm } from "@/screens/Pricing/components/forms/AddCostPackageForm"
import { usePriceChange } from "@/screens/Pricing/hooks/usePriceChange"
import type { CostPackageSchemaFormValues } from "@/screens/Pricing/schema/CostPackageSchema"
import { COST_PACKAGE_DEFAULT_FORM_VALUES, CostPackageSchema } from "@/screens/Pricing/schema/CostPackageSchema"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface AddCostPackageDialogProps {
  onClose: () => void
  onSuccess: () => void
  selectedProduct: Product
}

export const AddCostPackageDialog: FC<AddCostPackageDialogProps> = ({ onClose, onSuccess, selectedProduct }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const { addPurchasingCostPackage } = usePriceChange()
  const methods = useForm({
    defaultValues: COST_PACKAGE_DEFAULT_FORM_VALUES,
    mode: "onChange",
    resolver: zodResolver(CostPackageSchema),
  })

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState<boolean>(false)

  const {
    formState: { isDirty, isValid },
  } = methods

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
      return
    }
    onClose()
  }

  const handleSuccess = async ({
    warehouses,
    freightTerms,
    incoterms,
    incotermsLocationId,
  }: CostPackageSchemaFormValues) => {
    const results = await Promise.all(
      warehouses.map(({ shipToWarehouseAreaId }) =>
        addPurchasingCostPackage({
          input: {
            productId: selectedProduct.productId,
            shipToId: shipToWarehouseAreaId,
            incoterms: incoterms as string,
            incotermsLocationId,
            freightTerm: freightTerms as FreightTerms,
          },
        })
      )
    )

    const errorMessage = results.find((result) => result.error?.message)?.error?.message
    const hasSuccess = results.some((result) => !result.error?.message)

    if (errorMessage) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: errorMessage,
      })
      if (hasSuccess) {
        onSuccess()
      }
      return
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      message: warehouses.length > 1 ? "Cost packages added" : "Cost package added",
    })
    onSuccess()
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      )}
      <Dialog open onClose={handleCancel} fullWidth data-testid='pricing.add-cost-package-dialog'>
        <DialogHeader title='Add Cost Package' onClose={handleCancel} />
        <DialogContent classes={{ root: "p-10" }}>
          <FormProvider {...methods}>
            <AddCostPackageForm productId={selectedProduct.productId} />
          </FormProvider>
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10'>
          <Button
            appearance='outlined'
            onClick={handleCancel}
            data-testid='pricing.add-cost-package-dialog.cancel-button'
          >
            Cancel
          </Button>
          <Button
            disabled={!isValid || !isDirty}
            onClick={methods.handleSubmit(handleSuccess)}
            data-testid='pricing.add-cost-package-dialog.submit-button'
          >
            Add Cost Package
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
